/**
 * Clinical Supervisor — practice sessions queue.
 *
 * Lists therapist-practice TrainingSessions for the supervisor's assigned
 * therapists. Tabs: Awaiting release / Released / Auto-visible (no gate)
 * / QA-only. Click a row to drill into the (lecturer) review page where
 * AI feedback can be released.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Button,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  OpenInNew as OpenIcon,
  CheckCircle as ReleasedIcon,
  HourglassTop as AwaitingIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { verdictColor } from '../../utils/trainingVerdict';

const BUCKET_LABEL = {
  awaiting: 'Awaiting release',
  released: 'Released',
  auto: 'Auto-visible',
  qa: 'QA-only',
};

const formatDate = (s) => s ? new Date(s).toLocaleString() : '—';
const formatDuration = (sec) => sec ? `${Math.round(sec / 60)} min` : '—';

function SupervisorPracticeSessionsView({ token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [counts, setCounts] = useState({ awaiting: 0, released: 0, auto: 0, qa: 0, total: 0 });
  const [tab, setTab] = useState('awaiting');

  const load = useCallback(async (bucket) => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(
        `/simulation-governance/supervisor/practice-sessions${bucket && bucket !== 'all' ? `?released=${bucket}` : ''}`,
        token
      );
      const body = res?.data || res;
      setSessions(body?.sessions || []);
      setCounts(body?.counts || { awaiting: 0, released: 0, auto: 0, qa: 0, total: 0 });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(tab); }, [load, tab]);

  const goReview = (sessionId) => {
    navigate(`/training/lecturer/session/${sessionId}`, {
      state: { from: '/clinical-supervisor/practice-sessions' },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/clinical-supervisor/dashboard')} size="small">
          <BackIcon />
        </IconButton>
        <SchoolIcon color="primary" />
        <Typography variant="h5" sx={{ flex: 1 }}>Practice sessions</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => load(tab)} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Therapist-practice sessions across your assigned therapists. Sessions whose feedback visibility is
        <em> supervisor-gated</em> wait here for your review before AI scoring is shown to the therapist.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab
            value="awaiting"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AwaitingIcon fontSize="small" />
                Awaiting release ({counts.awaiting || 0})
              </Box>
            }
          />
          <Tab
            value="released"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReleasedIcon fontSize="small" />
                Released ({counts.released || 0})
              </Box>
            }
          />
          <Tab value="auto" label={`Auto-visible (${counts.auto || 0})`} />
          <Tab value="qa" label={`QA-only (${counts.qa || 0})`} />
          <Tab value="all" label={`All (${counts.total || 0})`} />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Therapist</TableCell>
                <TableCell>Scenario</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Visibility</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No sessions in this bucket.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {sessions.map(s => (
                <TableRow key={s._id} hover>
                  <TableCell>
                    <Typography variant="body2">{s._studentName || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{s._scenarioName || '—'}</Typography>
                    {s._scenarioDifficulty && (
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {s._scenarioDifficulty}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.status?.replace('_', ' ') || '—'}
                      color={s.status === 'completed' ? 'success' : s.status === 'in_progress' ? 'warning' : 'default'}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip size="small"
                      label={BUCKET_LABEL[s._bucket] || s.feedbackVisibility || '—'}
                      color={
                        s._bucket === 'awaiting' ? 'warning'
                          : s._bucket === 'released' ? 'success'
                          : s._bucket === 'auto' ? 'info'
                          : 'default'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {s.aiEvaluation?.compositePercentage != null
                      ? <Chip size="small"
                          label={`${Math.round(s.aiEvaluation.compositePercentage)}%`}
                          color={verdictColor(s.aiEvaluation.passFail)}
                          variant="outlined" />
                      : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(s.completedAt || s.startedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDuration(s.durationSeconds)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" startIcon={<OpenIcon />} onClick={() => goReview(s._id)}>
                      {s._bucket === 'awaiting' ? 'Review' : 'Open'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default SupervisorPracticeSessionsView;
